// @flow

import React from 'react';
import { connect } from 'react-redux';

import { Delay } from '@zapier/common-components';
import { RouterSelectors } from '@zapier/toolbox-redux-framework';

import { Profile } from '@zapier/shared-entities';
import NPSSurvey from 'app/common/components/NPSSurvey/NPSSurvey';
import TrackActions from 'app/track/TrackActions';

const hasNPSQueryParam = state =>
  !!RouterSelectors.getQueryParam(state, '_nps');

const isViewingSharedZapConvSetup = state => {
  const currentPathname = RouterSelectors.getCurrentPathname(state);
  const isViewingSharedZap = /^(?:\/app)*\/shared\//.test(currentPathname);
  const hasConvSetupFlag = !!RouterSelectors.getQueryParam(state, 'conv');
  return isViewingSharedZap && hasConvSetupFlag;
};

type Props = {
  currentProfileId: number,
  shouldRender: boolean,
  onDismissNPSSurvey: (currentProfileId: number) => void,
  onMountNPSSurvey: () => void,
  onSubmitNPSFeedback: (currentProfileId: number, value: number) => void,
  onUnmountNPSSurvey: (currentProfileId: number) => void,
};

const mapStateToProps = state => {
  const currentProfile = Profile.selectors.all.currentProfile(state);
  const currentProfileId = Profile.selectors.all.currentProfileId(state);

  // We store information on the users profile to determine if they
  // should see the survey or not so that we are not constantly hounding
  // the user with it. See the following backend code to see what makes them
  // eligible: https://github.com/zapier/zapier/blob/master/web/backend/zapier/customuser/models.py#L751
  // To force the survey to display, you can add `?_nps=true` to the URL.
  const shouldSeeNPSSurvey =
    !isViewingSharedZapConvSetup(state) &&
    (Profile.selectors.shouldSeeNPSSurvey(currentProfile) ||
      hasNPSQueryParam(state));

  return {
    currentProfileId,
    shouldRender: shouldSeeNPSSurvey,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  onMountNPSSurvey: () => {
    dispatch(TrackActions.track('nps tracker: seen'));
  },

  onDismissNPSSurvey: currentProfileId => {
    dispatch(TrackActions.track('nps tracker: dismiss'));
    dispatch(Profile.actions.acknowledgeNPSSurvey(currentProfileId));
  },

  onSubmitNPSFeedback: (currentProfileId, value) => {
    dispatch(TrackActions.track('nps tracker: submit', { value }));
    dispatch(Profile.actions.acknowledgeNPSSurvey(currentProfileId));
  },

  onUnmountNPSSurvey: currentProfileId => {
    dispatch(Profile.actions.acknowledgeNPSSurvey(currentProfileId));
  },
});

// It's not typical to render in the container like this, but we're doing it
// this way to prevent rendering the component and triggering the `seen` event
// in cases where `shouldRender` is falsy and we aren't actually rending the
// NPS Survey.
const Container = (props: Props) => {
  if (!props.shouldRender) {
    return null;
  }

  return (
    <Delay delay={5000}>
      <NPSSurvey
        onDismiss={() => {
          props.onDismissNPSSurvey(props.currentProfileId);
        }}
        onMount={() => {
          props.onMountNPSSurvey();
        }}
        onSubmit={value => {
          props.onSubmitNPSFeedback(props.currentProfileId, value);
        }}
        onUnmount={() => {
          props.onUnmountNPSSurvey(props.currentProfileId);
        }}
      />
    </Delay>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(Container);
